import {FormRepository, ScoreCalculator} from "domain/ports";
import {Guide, Question} from "domain/entities";

/**
 * Servicio para manejar la lógica de resultados
 */
export class ResultsService {
    private guides: { [key: string]: Guide } = {};

    constructor(
        private formRepository: FormRepository,
        private scoreCalculator: ScoreCalculator,
        initialGuides?: { [key: string]: Guide | null | undefined }
    ) {
        if (initialGuides) {
            // Filtrar cualquier guía nula o indefinida
            Object.entries(initialGuides).forEach(([key, guide]) => {
                if (guide) {
                    this.guides[key] = guide;
                }
            });
        }
    }

    /**
     * Obtiene todas las preguntas de una guía, incluyendo las dependientes
     * @param formKey Clave de la guía
     */
    async getAllQuestions(formKey: string): Promise<Question[]> {
        const guide = await this.formRepository.getGuideByKey(formKey);
        if (!guide) return [];

        let allQuestions: Question[] = [...(guide.questions || [])];

        // Añadir preguntas dependientes de serviceToClients si existen
        if (guide.serviceToClients?.dependentQuestions) {
            allQuestions = [
                ...allQuestions,
                ...guide.serviceToClients.dependentQuestions,
            ];
        }

        // Añadir preguntas dependientes de isBoss si existe (ahora como propiedad independiente)
        if (guide.isBoss?.dependentQuestions) {
            allQuestions = [...allQuestions, ...guide.isBoss.dependentQuestions];
        }

        return allQuestions;
    }

    /**
     * Obtiene las puntuaciones guardadas
     * @param guiaKey Clave principal de la guía
     * @param formKey Clave de la guía específica
     */
    async getScores(
        guiaKey: string,
        formKey: string
    ): Promise<{ [key: string]: number }> {
        try {
            // Intentar obtener puntuaciones con el formato principal
            const scores = await this.formRepository.getScores(guiaKey, formKey);

            // Si hay puntuaciones, devolverlas
            if (scores && Object.keys(scores).length > 0) {
                return scores;
            }

            // Si no hay puntuaciones, intentar buscar en localStorage
            return this.findScoresInLocalStorage(formKey);
        } catch (error) {
            console.error("Error getting scores:", error);
            return this.findScoresInLocalStorage(formKey);
        }
    }

    /**
     * Calcula los totales por categoría
     * @param formKey Clave de la guía
     */
    async calculateCategoryTotals(
        formKey: string
    ): Promise<{ [category: string]: number }> {
        const guide = this.guides[formKey];
        const scores = await this.getScores("NOM035", formKey);

        if (guide?.questions) {
            return this.scoreCalculator.calculateCategoryTotals(
                scores,
                guide.questions
            );
        }

        // Si la guía no está en la caché, buscar en el repositorio
        const questions = await this.formRepository.getQuestions(formKey);
        return this.scoreCalculator.calculateCategoryTotals(scores, questions);
    }

    /**
     * Calcula los totales por dominio
     * @param formKey Clave de la guía
     */
    async calculateDomainTotals(
        formKey: string
    ): Promise<{ [domain: string]: number }> {
        const guide = this.guides[formKey];
        const scores = await this.getScores("NOM035", formKey);

        if (guide?.questions) {
            return this.scoreCalculator.calculateDomainTotals(
                scores,
                guide.questions
            );
        }

        // Si la guía no está en la caché, buscar en el repositorio
        const questions = await this.formRepository.getQuestions(formKey);
        return this.scoreCalculator.calculateDomainTotals(scores, questions);
    }

    /**
     * Calcula los totales por dimensión
     * @param formKey Clave de la guía
     */
    async calculateDimensionTotals(
        formKey: string
    ): Promise<{ [dimension: string]: number }> {
        const guide = this.guides[formKey];
        const scores = await this.getScores("NOM035", formKey);

        if (guide?.questions) {
            return this.scoreCalculator.calculateDimensionTotals(
                scores,
                guide.questions
            );
        }

        // Si la guía no está en la caché, buscar en el repositorio
        const questions = await this.formRepository.getQuestions(formKey);
        return this.scoreCalculator.calculateDimensionTotals(scores, questions);
    }

    /**
     * Calcula la suma total de todas las puntuaciones
     * @param formKey Clave de la guía
     */
    async calculateTotalSum(formKey: string): Promise<number> {
        const scores = await this.getScores("NOM035", formKey);
        return Object.values(scores).reduce((sum, score) => sum + (score || 0), 0);
    }

    /**
     * Genera los datos para la tabla de resultados
     * @param formKey Clave de la guía
     */
    async generateResultsTable(formKey: string): Promise<
        Array<{
            questionId: string;
            category: string;
            domain: string;
            dimension: string;
            score: number;
            inverted: boolean;
            text: string;
        }>
    > {
        const [questions, scores] = await Promise.all([
            this.getAllQuestions(formKey),
            this.getScores("NOM035", formKey),
        ]);

        // Usar un Map para eliminar duplicados manteniendo el primer registro
        const uniqueQuestions = new Map();
        questions.forEach((question) => {
            if (!uniqueQuestions.has(question.id)) {
                uniqueQuestions.set(question.id, question);
            } else {
                console.warn(
                    `Se encontró una pregunta duplicada con ID: ${question.id}`
                );
            }
        });

        // Convertir el Map de nuevo a array y mapear los resultados
        return Array.from(uniqueQuestions.values()).map((question) => ({
            questionId: question.id,
            category: this.capitalize(question.category) || "Sin categoría",
            domain: this.capitalize(question.domain) || "Sin dominio",
            dimension: this.capitalize(question.dimension) || "Sin dimensión",
            score: scores[question.id] || 0,
            inverted: question.inverted || false,
            text: question.text,
        }));
    }

    capitalize(str: string) {
        if (!str) {
            return str;
        }
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    /**
     * Busca puntuaciones en localStorage con diferentes formatos de clave
     * @param formKey Clave de la guía específica
     */
    private findScoresInLocalStorage(formKey: string): { [key: string]: number } {
        const combinedScores: { [key: string]: number } = {};

        // Buscar en todas las claves de localStorage
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            //if (key && (key.includes('_scores') || key.includes('_answers'))) {
            // Solo procesar las claves que contienen el formKey y que terminan en _scores o _answers
            if (
                key &&
                key.includes(formKey) &&
                (key.includes("_scores") || key.includes("_answers"))
            ) {
                try {
                    const value = localStorage.getItem(key);
                    if (value) {
                        const parsed = JSON.parse(value);

                        // Si es un objeto con pares clave-valor, procesar como scores
                        if (
                            typeof parsed === "object" &&
                            parsed !== null &&
                            !Array.isArray(parsed)
                        ) {
                            Object.entries(parsed).forEach(([id, val]) => {
                                // Asegurarse de que sea un número válido
                                if (typeof val === "number" && !isNaN(val as number)) {
                                    if (!combinedScores[id] || combinedScores[id] === 0) {
                                        combinedScores[id] = val as number;
                                    }
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.error(`Error processing localStorage key ${key}:`, error);
                }
            }
        }
        return combinedScores;
    }
}
